import React, { useCallback, useEffect, useState } from "react";

import { Icon } from "@iconify/react/dist/iconify.js";
import { Tooltip } from "@mui/material";
import { usePostValidateDuplications } from "api/usePostValidateDuplications";
import removeIcon from "assets/removeIcon.svg";
import {
  AlertColor,
  MAX_FILE_UPLOAD,
  MAX_FILES_SIZE,
  MAX_TXT_CHARACTERS,
  OVERRIDE_MESSAGE,
} from "util/constants";
import { DocumentData } from "util/interfaces/DocumentData";
import { FileData } from "util/interfaces/FileData";

import InputDrop from "../../InputDrop/InputDrop";

import { validateTxtCaracters } from "@/utils/fileUtils";

interface StepOneProps {
  callbackError: (_message: string, _alertColor: AlertColor, _disable?: boolean) => void;
  callbackSetFiles: (_files: FileData[]) => void;
  existingFiles: FileData[];
  documents: DocumentData[];
  selectedPath: { id: string; name: string }[];
}

const StepOne: React.FC<StepOneProps> = ({
  callbackError,
  callbackSetFiles,
  existingFiles,
  documents,
  selectedPath,
}) => {
  const { mutateAsync: callValidateDuplicate } = usePostValidateDuplications({ callbackError });

  const [files, setFiles] = useState<FileData[]>(existingFiles ?? []);

  const validateFiles = (
    acceptedFiles: File[],
    callbackError: (_message: string, _color: AlertColor) => void
  ) => {
    const validFiles = acceptedFiles.filter((newFile) => {
      if (newFile.size > MAX_FILES_SIZE * 1024 * 1024) {
        callbackError(
          `File too large: ${newFile.name}. Maximum size is ${MAX_FILES_SIZE}MB.`,
          AlertColor.Error
        );
        return false;
      }
      return true;
    });

    return validFiles;
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: any[]) => {
      if (fileRejections.length > 0) {
        callbackError("Not supported file.", AlertColor.Error);
        return false;
      }
      //txt file validation
      const textFileCharacterCounts = await validateTxtCaracters(acceptedFiles);
      if (textFileCharacterCounts.some((file) => file.characters > MAX_TXT_CHARACTERS)) {
        callbackError(
          `This .txt file is too large to process (over 1 million characters). Please split it into smaller files and try again`,
          AlertColor.Error,
          files.length == 0
        );
        return false;
      }
      const newFiles = validateFiles(acceptedFiles, callbackError);
      let totalFileSize =
        newFiles.reduce((acum, f) => acum + f.size, 0) +
        files.reduce((acum, f) => acum + f.file.size, 0);
      totalFileSize = totalFileSize / 1024 / 1024;
      if (totalFileSize > MAX_FILES_SIZE) {
        callbackError(
          `Total files size exceed the maximum that is ${MAX_FILES_SIZE}MB.`,
          AlertColor.Error,
          files.length == 0
        );
        return false;
      }
      if (newFiles.length > 0) {
        const fileNames = newFiles.map((file) => file.name);
        const validationResults = await callValidateDuplicate({
          fileNames: fileNames,
          path: selectedPath.map((p) => p.name).join("/"),
        });

        const existsMap = new Map<string, boolean>();
        for (const result of validationResults) {
          existsMap.set(result.name, result.exists);
        }

        const documentMap = new Map<string, DocumentData>();
        for (const doc of documents) {
          documentMap.set(doc.path + doc.documentName, doc);
        }
        const updatedFiles = newFiles.map((newFile) => {
          const isDuplicate = files.some((existingFile) => existingFile.file.name === newFile.name);
          const path = selectedPath.length > 0 ? selectedPath.map((p) => p.name).join("/") : "";
          const matchingDocument = documentMap.get(path + newFile.name);
          if (isDuplicate) {
            callbackError("Remove duplicate file to continue.", AlertColor.Warning);
          }
          let fileExists = existsMap.get(newFile.name) ?? false;
          if (fileExists) {
            const fileDuplicatedExists = documents.find((d) => {
              const newFileNameParts = newFile.name.split(".");
              return (
                d.documentName === newFile.name && d.file_extension === newFileNameParts.at(-1)
              );
            });

            if (fileDuplicatedExists) {
              callbackError(OVERRIDE_MESSAGE, AlertColor.Warning);
            } else {
              fileExists = false; // If the file is not duplicated, we don't want to override
            }
          }
          return {
            file: newFile,
            fileCategory: matchingDocument ? matchingDocument.category : "",
            metadata: matchingDocument ? matchingDocument.metadata : "",
            isCompleted: !!matchingDocument,
            duplicated: isDuplicate,
            override: fileExists,
            uuid: matchingDocument ? matchingDocument.uuid : "",
          };
        });
        if ([...files, ...updatedFiles].length > MAX_FILE_UPLOAD) {
          callbackError(
            `The maximum number of files you can upload is ${MAX_FILE_UPLOAD}`,
            AlertColor.Warning,
            false
          );
        } else {
          setFiles((prevFiles) => [...prevFiles, ...updatedFiles]);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [files]
  );

  const removeFile = (index: number) => {
    setFiles(files.filter((f, idx) => idx !== index));
  };

  useEffect(() => {
    callbackSetFiles(files);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  return (
    <div className="grid-files" style={{ paddingTop: "3rem", height: "90%" }} data-testid="stepOne">
      <div style={{ gridArea: "drop-input" }}>
        <InputDrop callbackDrop={onDrop} maxSizeInMB={MAX_FILES_SIZE} height="99%" />
      </div>
      <div className="files_container">
        <div className="files_container_header">
          <div className="flex align-items-center gap-5">
            <h1>Selected files</h1>
            {/* <span className="font-10">{`${Math.round(files.reduce((acum, f) => acum + f.file.size, 0) / 1024 / 1024)} MB`}</span> */}
          </div>
        </div>
        <div className="files_container_content">
          {files.length == 0 ? (
            <h6>No files selected</h6>
          ) : (
            <>
              {files.map((file, index) => (
                <div className="file_list_slide1" key={file.file.name}>
                  <div data-testid="fileSection" className="file_title_list">
                    <Tooltip title={file ? file.file.name : null} arrow>
                      <p
                        style={{
                          color: file.duplicated ? "red" : "black",
                          fontWeight: file.override ? "600" : "400",
                          maxWidth: "20vw",
                        }}
                      >
                        {file.file.name}
                      </p>
                    </Tooltip>

                    {file.override && (
                      <Tooltip title={OVERRIDE_MESSAGE} arrow>
                        <Icon
                          icon="ep:warning-filled"
                          height={15}
                          style={{ color: "black", marginLeft: "10px" }}
                          aria-label={`Warning: ${file.file.name} will override existing file`}
                        />
                      </Tooltip>
                    )}
                  </div>
                  <button className="remove_file_btn" onClick={() => removeFile(index)}>
                    <img src={removeIcon} height={30} alt="remove_icon" />
                  </button>
                </div>
              ))}

              <button
                onClick={() => {
                  setFiles([]);
                }}
                className="reset_btn"
              >
                <h4>Reset</h4>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StepOne;
